import { parseDecimalInputValue } from '@/components/form/DecimalInput'
import type { Address } from '@/lib/services/addresses/addresses.types'
import type { DeliveryAreaFee } from '@/lib/services/locations/locations.types'
import type { Polygon } from 'geojson'
import { useCallback, useEffect, useRef, type RefObject } from 'react'
import {
  TerraDraw,
  TerraDrawPolygonMode,
  TerraDrawSelectMode,
  type GeoJSONStoreFeatures,
  type HexColor,
} from 'terra-draw'
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter'
import { getGoogleMapsMapId } from '@/lib/google-maps-api-key'
import { DEFAULT_DELIVERY_AREA_FILL_OPACITY, DELIVERY_AREA_COLORS } from '../constants'
import { getMapsUtilities, isValidCoordinate, type MapsFallback } from '../utils'

/** The address the map marks: the location's. */
export type MapLocationAddress = Pick<
  Address,
  'lat' | 'lng' | 'line1' | 'city' | 'state' | 'postal_code'
>

/** The address's saved coordinates, which the API sends as decimal strings, or null without both. */
const getSavedCoordinates = (
  address: MapLocationAddress | null
): google.maps.LatLngLiteral | null => {
  const lat = parseDecimalInputValue(address?.lat)
  const lng = parseDecimalInputValue(address?.lng)

  return lat === null || lng === null ? null : { lat, lng }
}

type FeatureProperty = GeoJSONStoreFeatures['properties'][string]

/** A polygon feature Terra Draw finished drawing. */
export type DrawnPolygonFeature = GeoJSONStoreFeatures<Polygon>

const isPolygonFeature = (feature: GeoJSONStoreFeatures): feature is DrawnPolygonFeature =>
  feature.geometry.type === 'Polygon'

const isHexColor = (value: FeatureProperty | undefined): value is HexColor =>
  typeof value === 'string' && value.startsWith('#')

const readHexColor = (value: FeatureProperty | undefined, fallback: HexColor): HexColor =>
  isHexColor(value) ? value : fallback

const readNumber = (value: FeatureProperty | undefined, fallback: number): number =>
  typeof value === 'number' ? value : fallback

interface UseMapSetupOptions {
  isLoaded: boolean
  maps: google.maps.MapsLibrary | null
  geometry: google.maps.GeometryLibrary | null
  marker: google.maps.MarkerLibrary | null
  mapRef: RefObject<HTMLDivElement | null>
  mapInstanceRef: RefObject<google.maps.Map | null>
  locationMarkerRef: RefObject<google.maps.marker.AdvancedMarkerElement | null>
  terraDrawRef: RefObject<TerraDraw | null>
  locationAddress: MapLocationAddress | null
  initialPolygons: Pick<DeliveryAreaFee, 'encoded_polygon'>[]
  handlePolygonComplete: (feature: DrawnPolygonFeature) => void
  drawingColor: HexColor | null
  drawingFillOpacity?: number
  onTerraDrawReady?: () => void
}

/**
 * Hook for initializing the map, location marker, and Terra Draw
 */
export const useMapSetup = ({
  isLoaded,
  maps,
  geometry,
  marker,
  mapRef,
  mapInstanceRef,
  locationMarkerRef,
  terraDrawRef,
  locationAddress,
  initialPolygons,
  handlePolygonComplete,
  drawingColor, // Color to use when drawing new polygons
  drawingFillOpacity = DEFAULT_DELIVERY_AREA_FILL_OPACITY,
  onTerraDrawReady,
}: UseMapSetupOptions): void => {
  // Track map initialization for re-running effects (use ref to avoid setState in effect)
  const mapInitializedRef = useRef(false)
  // Store latest handlePolygonComplete to avoid stale closures in event listeners
  const handlePolygonCompleteRef = useRef(handlePolygonComplete)
  // Store the current drawing color so style functions can access the latest value
  const drawingColorRef = useRef<HexColor>(drawingColor || DELIVERY_AREA_COLORS[0])
  const drawingFillOpacityRef = useRef(drawingFillOpacity)

  useEffect(() => {
    return () => {
      if (locationMarkerRef.current) {
        locationMarkerRef.current.map = null
        locationMarkerRef.current = null
      }

      if (terraDrawRef.current) {
        try {
          terraDrawRef.current.stop()
        } catch (err) {
          console.error('Error stopping Terra Draw:', err)
        }
        terraDrawRef.current = null
      }

      mapInstanceRef.current = null
    }
  }, [locationMarkerRef, mapInstanceRef, terraDrawRef])

  // Keep the ref updated with the latest callback
  useEffect(() => {
    handlePolygonCompleteRef.current = handlePolygonComplete
  }, [handlePolygonComplete])

  // Helper function to add location marker
  const addLocationMarker = useCallback(
    (
      coordinates: { lat: unknown; lng: unknown } | null,
      title: string,
      mapsLib: MapsFallback | null,
      mapInstance: google.maps.Map | null
    ) => {
      if (!mapsLib || !mapInstance || !marker) return

      // Validate coordinates before using them
      if (
        !coordinates ||
        !isValidCoordinate(coordinates.lat) ||
        !isValidCoordinate(coordinates.lng)
      ) {
        console.error('Invalid coordinates for marker:', coordinates)
        return
      }

      // Remove existing marker if any
      if (locationMarkerRef.current) {
        locationMarkerRef.current.map = null
        locationMarkerRef.current = null
      }

      const utils = getMapsUtilities(mapsLib, marker)
      const { AdvancedMarkerElement, PinElement, LatLng } = utils
      if (!AdvancedMarkerElement) {
        console.error('AdvancedMarkerElement not available')
        return
      }

      // Ensure coordinates are numbers
      const validCoordinates = {
        lat: Number(coordinates.lat),
        lng: Number(coordinates.lng),
      }

      // Create a LatLng object for the position
      const position = LatLng
        ? new LatLng(validCoordinates.lat, validCoordinates.lng)
        : validCoordinates

      // Create a custom pin element with the red color
      let pinElement: google.maps.marker.PinElement | null = null
      if (PinElement) {
        pinElement = new PinElement({
          background: '#EA4335',
          borderColor: '#FFFFFF',
          glyphColor: '#FFFFFF',
          scale: 1,
        })
      }

      // Create the advanced marker
      locationMarkerRef.current = new AdvancedMarkerElement({
        map: mapInstance,
        position: position,
        title: title || 'Location',
        content: pinElement || null,
      })
    },
    [locationMarkerRef, marker]
  )

  // Initialize map
  useEffect(() => {
    if (!isLoaded || !maps || mapRef.current === null || mapInstanceRef.current !== null) {
      return
    }

    let defaultCenter: google.maps.LatLngLiteral = { lat: 40.7128, lng: -74.006 }
    let defaultZoom = 10
    let locationCoordinates: google.maps.LatLngLiteral | null = null
    let addressString = ''
    let shouldFitBounds = false
    let polygonBounds: google.maps.LatLngBounds | null = null

    const extendBoundsWithLocation = (
      bounds: google.maps.LatLngBounds | null,
      coordinates: google.maps.LatLngLiteral | null
    ) => {
      if (
        !bounds ||
        !coordinates ||
        !isValidCoordinate(coordinates.lat) ||
        !isValidCoordinate(coordinates.lng)
      ) {
        return false
      }

      const utils = getMapsUtilities(maps)
      const { LatLng } = utils
      const lat = Number(coordinates.lat)
      const lng = Number(coordinates.lng)

      bounds.extend(LatLng ? new LatLng(lat, lng) : { lat, lng })
      return true
    }

    // If there are initial polygons, calculate bounds to fit them
    if (initialPolygons && initialPolygons.length > 0 && geometry && geometry.encoding) {
      const utils = getMapsUtilities(maps)
      const { LatLngBounds } = utils
      if (LatLngBounds) {
        const bounds = new LatLngBounds()
        polygonBounds = bounds
        let hasValidBounds = false

        initialPolygons.forEach((polygonData) => {
          try {
            const encodedPath = polygonData.encoded_polygon || polygonData
            if (encodedPath && typeof encodedPath === 'string') {
              const path = geometry.encoding.decodePath(encodedPath)
              path.forEach((latLng) => {
                bounds.extend(latLng)
                hasValidBounds = true
              })
            }
          } catch (err) {
            console.error('Error decoding polygon for initial bounds:', err)
          }
        })

        if (hasValidBounds) {
          shouldFitBounds = true
        }
      }
    }

    // Get location coordinates for marker (always add marker if address is available)
    // First, try to get coordinates directly from address
    const savedCoordinates = getSavedCoordinates(locationAddress)
    if (locationAddress && savedCoordinates) {
      locationCoordinates = savedCoordinates
      // Only use as center if we're not fitting to polygon bounds
      if (!shouldFitBounds) {
        defaultCenter = locationCoordinates
      } else if (polygonBounds) {
        extendBoundsWithLocation(polygonBounds, locationCoordinates)
      }
      // Build address string for marker title
      addressString =
        [
          locationAddress.line1,
          locationAddress.city,
          locationAddress.state,
          locationAddress.postal_code,
        ]
          .filter(Boolean)
          .join(', ') || 'Location'
    } else if (locationAddress) {
      // If no direct coordinates, try geocoding
      // We still need to geocode to add the location marker even when fitting to polygon bounds
      const utils = getMapsUtilities(maps)
      const { Geocoder } = utils
      if (Geocoder) {
        const geocoder = new Geocoder()
        // Use full address instead of just postal code
        addressString = [
          locationAddress.line1,
          locationAddress.city,
          locationAddress.state,
          locationAddress.postal_code,
        ]
          .filter(Boolean)
          .join(', ')

        if (addressString) {
          void geocoder.geocode({ address: addressString }).then(
            ({ results }) => {
              const [firstResult] = results
              if (!firstResult || !mapInstanceRef.current) {
                return
              }

              const location = firstResult.geometry.location
              const lat = location.lat()
              const lng = location.lng()

              // Validate coordinates before using them
              if (isValidCoordinate(lat) && isValidCoordinate(lng)) {
                locationCoordinates = { lat: Number(lat), lng: Number(lng) }
                // Only set center if we're not fitting bounds
                if (!shouldFitBounds) {
                  mapInstanceRef.current.setCenter(locationCoordinates)
                  mapInstanceRef.current.setZoom(12)
                }

                // Add marker for location address (always add, even when fitting to polygon bounds)
                addLocationMarker(locationCoordinates, addressString, maps, mapInstanceRef.current)

                if (shouldFitBounds && polygonBounds) {
                  extendBoundsWithLocation(polygonBounds, locationCoordinates)
                  mapInstanceRef.current.fitBounds(polygonBounds, 50)
                }
              } else {
                console.error('Invalid coordinates from geocoding:', { lat, lng })
              }
            },
            // An address Google can't place leaves the default view; no one needs to act on it, and a
            // failure inside the success branch still surfaces.
            () => undefined
          )
        }
      }
    }

    // Disable default map type control - we'll use a custom compact one
    // Map ID is required for Advanced Markers
    const mapId = getGoogleMapsMapId()
    mapInstanceRef.current = new maps.Map(mapRef.current, {
      center: defaultCenter,
      zoom: defaultZoom,
      mapId: mapId,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      clickableIcons: false,
    })

    // Mark map as initialized for dependent effects
    mapInitializedRef.current = true

    // If we have polygon bounds, fit the map to them
    if (shouldFitBounds && polygonBounds && mapInstanceRef.current) {
      mapInstanceRef.current.fitBounds(polygonBounds, 50)
    }

    // Add marker for location address if coordinates are available (direct coordinates case)
    if (locationCoordinates && addressString) {
      addLocationMarker(locationCoordinates, addressString, maps, mapInstanceRef.current)
    }

    // Initialize Terra Draw after map is fully loaded
    // Wait for the map's 'idle' event to ensure it's ready
    const initializeTerraDraw = () => {
      if (!maps || !mapInstanceRef.current || !window.google?.maps) {
        return
      }

      // Check if Terra Draw is already initialized
      if (terraDrawRef.current) {
        return
      }

      try {
        const draw = new TerraDraw({
          adapter: new TerraDrawGoogleMapsAdapter({
            map: mapInstanceRef.current,
            lib: window.google.maps,
          }),
          modes: [
            new TerraDrawSelectMode({
              flags: {
                polygon: {
                  feature: {
                    draggable: false, // Prevent moving the entire polygon
                    rotateable: false, // Prevent rotating the entire polygon
                    scaleable: false, // Prevent scaling the entire polygon
                    coordinates: {
                      midpoints: true, // Allow adding vertices at mid-points
                      draggable: true, // Allow dragging individual vertices
                      deletable: true, // Allow deleting individual vertices
                    },
                  },
                },
              },
              // Reduce sensitivity to prevent accidental selection
              pointerDistance: 10, // Require being within 10 pixels to interact
              allowManualDeselection: false,
              // Dynamic styling for selected features based on feature properties
              styles: {
                selectedPolygonColor: (feature) =>
                  readHexColor(feature.properties?.fillColor, DELIVERY_AREA_COLORS[0]),
                selectedPolygonFillOpacity: (feature) =>
                  readNumber(feature.properties?.fillOpacity, 0.5),
                selectedPolygonOutlineColor: (feature) =>
                  readHexColor(feature.properties?.outlineColor, DELIVERY_AREA_COLORS[0]),
                selectedPolygonOutlineWidth: 3,
              },
            }),
            new TerraDrawPolygonMode({
              // Disable editing in polygon mode - we only want to create new polygons
              // Editing is handled in select mode
              editable: false,
              // Snapping stays off (Terra Draw's default) to prevent accidental vertex creation on nearby features
              // Dynamic styling - uses feature properties if available, falls back to current drawing color
              styles: {
                fillColor: (feature) =>
                  readHexColor(feature.properties?.fillColor, drawingColorRef.current),
                fillOpacity: (feature) =>
                  readNumber(feature.properties?.fillOpacity, drawingFillOpacityRef.current),
                outlineColor: (feature) =>
                  readHexColor(feature.properties?.outlineColor, drawingColorRef.current),
                outlineWidth: 2,
              },
            }),
          ],
        })

        // Set the ref immediately so it's available when ready event fires
        terraDrawRef.current = draw

        // Start Terra Draw
        draw.start()

        // Listen for ready event to ensure Terra Draw is fully initialized
        draw.on('ready', () => {
          // Notify parent component that Terra Draw is ready
          if (onTerraDrawReady) {
            onTerraDrawReady()
          }
        })

        // Listen for polygon completion
        draw.on('finish', (id, mode) => {
          // Mode can be an object like {mode: 'polygon', action: 'draw'} or a string
          const modeString = typeof mode === 'string' ? mode : mode?.mode
          if (modeString === 'polygon') {
            const features = draw.getSnapshot()
            const polygonFeature = features.find((f) => f.id === id)
            if (polygonFeature && isPolygonFeature(polygonFeature)) {
              // Use ref to get latest callback, avoiding stale closure
              handlePolygonCompleteRef.current?.(polygonFeature)
            }
          }
        })

        // Note: Change events for polygon editing are handled by usePolygonLoading hook
        // which registers its own 'change' event listener

        // Ref is already set above, but ensure it's still set here as a safety check
        if (!terraDrawRef.current) {
          terraDrawRef.current = draw
        }
      } catch (err) {
        console.error('Error initializing Terra Draw:', err)
      }
    }

    // Wait for map to be idle (fully loaded) before initializing Terra Draw
    const utils = getMapsUtilities(maps)
    let earlyInitTimeout: ReturnType<typeof setTimeout> | null = null
    let fallbackInitTimeout: ReturnType<typeof setTimeout> | null = null
    let idleListener: google.maps.MapsEventListener | null = null
    if (utils.event && mapInstanceRef.current) {
      // Use a one-time listener for the idle event
      const handleIdle = () => {
        initializeTerraDraw()
        if (idleListener && utils.event) {
          utils.event.removeListener(idleListener)
        }
      }

      idleListener = utils.event.addListener(mapInstanceRef.current, 'idle', handleIdle)

      // Also try to initialize immediately if map is already idle
      // Use a small timeout to ensure map div is fully rendered
      earlyInitTimeout = setTimeout(() => {
        if (!terraDrawRef.current) {
          initializeTerraDraw()
          // Remove the listener if we initialized early
          if (idleListener && utils.event) {
            utils.event.removeListener(idleListener)
          }
        }
      }, 100)
    } else {
      // Fallback: try to initialize after a short delay
      fallbackInitTimeout = setTimeout(initializeTerraDraw, 200)
    }

    return () => {
      if (earlyInitTimeout) {
        clearTimeout(earlyInitTimeout)
      }

      if (fallbackInitTimeout) {
        clearTimeout(fallbackInitTimeout)
      }

      if (idleListener && utils.event) {
        utils.event.removeListener(idleListener)
      }

      // Cleanup marker on unmount
      if (locationMarkerRef.current) {
        locationMarkerRef.current.map = null
        locationMarkerRef.current = null
      }
      // Cleanup Terra Draw
      // Only stop Terra Draw if we're actually unmounting or if the map instance is being destroyed
      // Don't stop it just because dependencies changed - this prevents unnecessary re-initialization
      if (terraDrawRef.current && !mapInstanceRef.current) {
        try {
          terraDrawRef.current.stop()
          terraDrawRef.current = null
        } catch (err) {
          console.error('Error stopping Terra Draw:', err)
        }
      }
    }
    // Stable refs and initial values only, locationAddress uses specific properties
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoaded,
    maps,
    geometry,
    marker,
    // Using specific address properties to avoid re-running on object reference changes:
    locationAddress?.lat,
    locationAddress?.lng,
    locationAddress?.line1,
    locationAddress?.postal_code,
    // initialPolygons should only be used on first mount
  ])

  // Effect to update location marker when address changes after map is initialized
  useEffect(() => {
    if (
      !maps ||
      !marker ||
      !mapInitializedRef.current ||
      !mapInstanceRef.current ||
      !locationAddress
    ) {
      return
    }

    const updateMarkerFromAddress = async () => {
      // Check if we have direct coordinates
      const coordinates = getSavedCoordinates(locationAddress)
      if (coordinates) {
        const addressString =
          [
            locationAddress.line1,
            locationAddress.city,
            locationAddress.state,
            locationAddress.postal_code,
          ]
            .filter(Boolean)
            .join(', ') || 'Location'

        addLocationMarker(coordinates, addressString, maps, mapInstanceRef.current)
      } else if (locationAddress.line1 || locationAddress.postal_code) {
        // Try geocoding
        const utils = getMapsUtilities(maps)
        const { Geocoder } = utils
        if (Geocoder) {
          try {
            const geocoder = new Geocoder()
            const addressString = [
              locationAddress.line1,
              locationAddress.city,
              locationAddress.state,
              locationAddress.postal_code,
            ]
              .filter(Boolean)
              .join(', ')

            if (addressString) {
              void geocoder.geocode({ address: addressString }).then(
                ({ results }) => {
                  const [firstResult] = results
                  if (firstResult) {
                    const loc = firstResult.geometry.location
                    const coordinates = {
                      lat: loc.lat(),
                      lng: loc.lng(),
                    }

                    addLocationMarker(coordinates, addressString, maps, mapInstanceRef.current)
                  }
                },
                // An address Google can't place adds no marker; no one needs to act on it, and a
                // failure inside the success branch still surfaces.
                () => undefined
              )
            }
          } catch (err) {
            console.error('Error geocoding address for marker update:', err)
          }
        }
      }
    }

    void updateMarkerFromAddress()
    // Use specific address properties to avoid re-running on object reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    maps,
    marker,
    mapInstanceRef,
    locationAddress?.lat,
    locationAddress?.lng,
    locationAddress?.line1,
    locationAddress?.city,
    locationAddress?.state,
    locationAddress?.postal_code,
    addLocationMarker,
    initialPolygons,
  ])

  // Effect to update polygon mode's drawing color when it changes
  // Keep the drawing color ref updated when it changes
  useEffect(() => {
    drawingColorRef.current = drawingColor || DELIVERY_AREA_COLORS[0]
    drawingFillOpacityRef.current = drawingFillOpacity
  }, [drawingColor, drawingFillOpacity])

  // Update polygon mode styles when drawing color changes
  // Use function-based styling that reads from feature properties first, then falls back to drawing color
  // This also fixes a bug existing polygons inherit the new polygon's color when polygons overlap
  useEffect(() => {
    if (!terraDrawRef.current || !drawingColor) {
      return
    }

    try {
      // Use function-based styling to preserve per-feature colors
      // Only use drawingColor for features that don't have their own color set
      terraDrawRef.current.updateModeOptions<typeof TerraDrawPolygonMode>('polygon', {
        styles: {
          fillColor: (feature) =>
            readHexColor(feature.properties?.fillColor, drawingColorRef.current),
          fillOpacity: (feature) =>
            readNumber(feature.properties?.fillOpacity, drawingFillOpacityRef.current),
          outlineColor: (feature) =>
            readHexColor(feature.properties?.outlineColor, drawingColorRef.current),
          outlineWidth: 2,
        },
      })
    } catch (err) {
      // Silently fail - this is an optimization, not critical
      console.debug('[useMapSetup] Could not update polygon mode styles:', err)
    }
  }, [drawingColor, drawingFillOpacity, terraDrawRef])
}
